import React, { ReactNode } from 'react';

import { FormControlLabel, Radio as MuiRadio, RadioProps as MuiRadioProps } from '@mui/material';

import styles from './Radio.module.scss';

export interface RadioProps extends Omit<MuiRadioProps, 'onChange'> {
    /**
     * The label for the radio button
     */
    label?: ReactNode;
    /**
     * If true, the radio button is checked
     */
    checked?: boolean;
    /**
     * The value of the radio button
     */
    value?: string | number;
    /**
     * The name used to reference the radio button (for grouping)
     */
    name?: string;
    /**
     * Callback fired when the radio button is clicked
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    /**
     * If true, the radio button will be disabled
     */
    disabled?: boolean;
    /**
     * Custom class name for the container
     */
    className?: string;
    /**
     * Position of the label relative to the radioButton
     */
    labelPlacement?: 'start' | 'end' | 'top' | 'bottom';
    /**
     * Custom color for the label text (overrides default theme color)
     */
    labelColor?: string;
}

/**
 * Custom Radio component based on MUI Radio
 * Implements design system styling from Figma atoms
 * Supports light and dark themes via CSS variables
 */
export const Radio: React.FC<RadioProps> = ({
    label,
    checked,
    value,
    name,
    onChange,
    disabled = false,
    className = '',
    labelPlacement = 'end',
    labelColor,
    ...props
}) => {
    const containerClass = [styles.container, disabled ? styles.disabled : '', className].filter(Boolean).join(' ');

    // Set CSS variable for label color if provided
    const containerStyle = labelColor ? ({ '--label-color': labelColor } as React.CSSProperties) : undefined;

    const Radio = (
        <MuiRadio
            className={styles.radio}
            checked={checked}
            value={value}
            name={name}
            onChange={onChange}
            disabled={disabled}
            {...props}
        />
    );

    // If there's a label, wrap in FormControlLabel
    if (label) {
        return (
            <FormControlLabel
                control={Radio}
                label={label}
                className={containerClass}
                disabled={disabled}
                style={containerStyle}
                labelPlacement={labelPlacement}
                classes={{
                    label: styles.label,
                }}
            />
        );
    }

    // Otherwise, just return the radio button
    return <div className={containerClass}>{Radio}</div>;
};

export default Radio;
